import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "cs_deck_token";
const SCOPE_KEY = "cs_deck_scope";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = sessionStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export function getToken() { return sessionStorage.getItem(TOKEN_KEY); }
export function getScope() { return sessionStorage.getItem(SCOPE_KEY); }
export function setSession(token, scope) {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  if (scope) sessionStorage.setItem(SCOPE_KEY, scope);
  // one-time migration: drop any token left behind in localStorage
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SCOPE_KEY);
}
export function clearToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(SCOPE_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SCOPE_KEY);
}

export async function login(code) {
  const { data } = await api.post("/auth/login", { code });
  if (data?.token) setSession(data.token, data.scope || "viewer");
  return data;
}

export async function prospectLogin(slug, code) {
  const { data } = await api.post(`/p/${slug}/login`, { code });
  // Backend now returns scope="master" if the admin code was entered on a
  // prospect gate — honour that instead of hard-coding "prospect".
  if (data?.token) setSession(data.token, data.scope || "prospect");
  return data;
}

export async function logout() {
  try { await api.post("/auth/logout"); } catch (e) { /* ignored — client-side logout proceeds */ }
  clearToken();
  return { ok: true };
}

export async function me() {
  const { data } = await api.get("/auth/me");
  return data;
}

export async function getProspectPublic(slug) {
  const { data } = await api.get(`/p/${slug}`);
  return data;
}

export async function submitContact(payload) {
  const { data } = await api.post("/contact", payload);
  return data;
}

export async function approvePrototype(payload) {
  const { data } = await api.post("/prototype/approve", payload);
  return data;
}

/* --------- Giggle Finance microsite -------- */
export async function submitGiggleOrder(payload) {
  const { data } = await api.post("/giggle/order", payload);
  return data;
}

export async function submitGiggleEngagement(payload) {
  const { data } = await api.post("/giggle/engagement", payload);
  return data;
}

export async function submitFlagstarEngagement(payload) {
  const { data } = await api.post("/flagstar/engagement", payload);
  return data;
}

export async function pingGiggleStar(addonIds, viewerEmail = null) {
  try {
    const { data } = await api.post("/giggle/star-ping", {
      addon_ids: addonIds,
      viewer_email: viewerEmail,
    });
    return data;
  } catch (e) {
    return null;
  }
}

/* --------- RTD Financial microsite -------- */
export async function submitRtdOrder(payload) {
  const { data } = await api.post("/rtd/order", payload);
  return data;
}

export async function submitRtdEngagement(payload) {
  const { data } = await api.post("/rtd/engagement", payload);
  return data;
}

export async function pingRtdStar(addonIds, viewerEmail = null) {
  try {
    const { data } = await api.post("/rtd/star-ping", {
      addon_ids: addonIds,
      viewer_email: viewerEmail,
    });
    return data;
  } catch (e) {
    return null;
  }
}

function downloadBlob(data, filename) {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function shareRtdPlan(payload) {
  const { data } = await api.post("/rtd/plans", payload);
  return data;
}

export async function getRtdPlan(planId) {
  const { data } = await api.get(`/rtd/plans/${planId}`);
  return data;
}

export async function downloadRtdPlanPdf(planId) {
  const { data } = await api.get(`/rtd/plans/${planId}.pdf`, { responseType: "blob" });
  downloadBlob(data, "RTD-Financial-CompliSun-Plan.pdf");
}

export async function decideRtdPlan(planId, payload) {
  const { data } = await api.post(`/rtd/plans/${planId}/decision`, payload);
  return data;
}

export async function submitThnbProposal(payload) {
  const { data } = await api.post("/thnb/proposal", payload);
  return data;
}

export async function downloadThnbSummary(config) {
  const { data } = await api.post("/thnb/summary.pdf", config, { responseType: "blob" });
  downloadBlob(data, "CompliSun-Proposal-Texas-Heritage-National-Bank.pdf");
}

export async function downloadRtdChecklist() {
  const { data } = await api.get("/rtd/migration-checklist.pdf", { responseType: "blob" });
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "RTD-Financial-Migration-Checklist.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  api.post("/rtd/checklist-ping", { viewer_email: null }).catch(() => null);
}

/* --------- Sage Home Loans microsite -------- */
export async function submitSageOrder(payload) {
  const { data } = await api.post("/sage/order", payload);
  return data;
}

export async function submitSageEngagement(payload) {
  const { data } = await api.post("/sage/engagement", payload);
  return data;
}

export async function pingSageStar(addonIds, viewerEmail = null) {
  try {
    const { data } = await api.post("/sage/star-ping", {
      addon_ids: addonIds,
      viewer_email: viewerEmail,
    });
    return data;
  } catch (e) {
    return null;
  }
}

/* --------- Accountable microsite -------- */
export async function submitAccountableOrder(payload) {
  const { data } = await api.post("/accountable/order", payload);
  return data;
}

export async function submitAccountableEngagement(payload) {
  const { data } = await api.post("/accountable/engagement", payload);
  return data;
}

export async function pingAccountableStar(addonIds, viewerEmail = null) {
  try {
    const { data } = await api.post("/accountable/star-ping", {
      addon_ids: addonIds,
      viewer_email: viewerEmail,
    });
    return data;
  } catch (e) {
    return null;
  }
}

/* --------- Portfolio Compliance Program microsite -------- */
export async function submitPortfolioContact(payload) {
  const { data } = await api.post("/portfolio/contact", payload);
  return data;
}

/* --------- Unified admin leads + content overrides -------- */
export async function listLeads(limit = 200) {
  const { data } = await api.get(`/admin/leads?limit=${limit}`);
  return data;
}

export async function getContentOverrides(ns) {
  const { data } = await api.get(`/content/${ns}`);
  return data;
}

export async function setContentOverride(ns, key, value) {
  const { data } = await api.patch(`/content/${ns}`, { key, value });
  return data;
}

export async function resetContentOverride(ns, key) {
  const { data } = await api.delete(`/content/${ns}/${encodeURIComponent(key)}`);
  return data;
}

/* --------- Portfolio saved quotes -------- */
export async function savePortfolioQuote(payload) {
  const { data } = await api.post(`/portfolio/quotes`, payload);
  return data;
}


export async function exportProspectsCsv() {
  const { data } = await api.get("/admin/prospects.csv", { responseType: "blob" });
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `complisun-prospects-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function listMessages() {
  const { data } = await api.get("/contact/messages");
  return data;
}

export async function listProspects() {
  const { data } = await api.get("/prospects");
  return data;
}

export async function createProspect(payload) {
  const { data } = await api.post("/prospects", payload);
  return data;
}

export async function updateProspectCode(id, code) {
  const { data } = await api.patch(`/prospects/${id}/code`, { code });
  return data;
}

export async function deleteProspect(id) {
  const { data } = await api.delete(`/prospects/${id}`);
  return data;
}

export async function sendProspectLink(id) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const { data } = await api.post(`/prospects/${id}/send-link`, { deck_origin: origin });
  return data;
}

export async function prospectAnalytics(id) {
  const { data } = await api.get(`/prospects/${id}/analytics`);
  return data;
}

export async function recordSlideEvent(slide_index, seconds) {
  try {
    await api.post("/analytics/slide", { slide_index, seconds });
  } catch (e) {
  }
}

export async function recordSectionEvent(section, seconds) {
  try {
    await api.post("/analytics/section", { section, seconds });
  } catch (e) {
  }
}

export async function listActivity(limit = 50) {
  const { data } = await api.get(`/activity?limit=${limit}`);
  return data;
}
